import {
  TOPPINGS,
  type EggStyle,
  type HashBrownSpec,
  type ItemKind,
  type OrderSpec,
  type Topping,
  type WaffleKind,
} from './types';

/*
 * Waffle House hash brown lingo (as printed on the real menu):
 *   scattered — spread out on the grill
 *   smothered — sautéed onions      covered  — melted cheese
 *   chunked   — hickory smoked ham  diced    — grilled tomatoes
 *   peppered  — jalapeño peppers    capped   — grilled mushrooms
 *   topped    — chili               country  — sausage gravy
 *   all the way — all eight toppings
 */

export interface ToppingInfo {
  id: Topping;
  lingo: string;
  ingredient: string;
  key: string;
}

export const TOPPING_INFO: Record<Topping, ToppingInfo> = {
  smothered: { id: 'smothered', lingo: 'SMOTHERED', ingredient: 'onions', key: 'A' },
  covered: { id: 'covered', lingo: 'COVERED', ingredient: 'cheese', key: 'S' },
  chunked: { id: 'chunked', lingo: 'CHUNKED', ingredient: 'ham', key: 'D' },
  diced: { id: 'diced', lingo: 'DICED', ingredient: 'tomatoes', key: 'F' },
  peppered: { id: 'peppered', lingo: 'PEPPERED', ingredient: 'jalapeños', key: 'G' },
  capped: { id: 'capped', lingo: 'CAPPED', ingredient: 'mushrooms', key: 'H' },
  topped: { id: 'topped', lingo: 'TOPPED', ingredient: 'chili', key: 'J' },
  country: { id: 'country', lingo: 'COUNTRY', ingredient: 'sausage gravy', key: 'K' },
};

export const WAFFLE_LABEL: Record<WaffleKind, string> = {
  classic: 'WAFFLE',
  pecan: 'PECAN WAFFLE',
};

export const EGG_LABEL: Record<EggStyle, string> = {
  scrambled: 'SCRAMBLED',
  overEasy: 'OVER EASY',
};

export const ITEM_LABEL: Record<ItemKind, string> = {
  hashBrowns: 'HASH BROWNS',
  waffle: 'WAFFLE',
  eggs: 'EGGS',
  bacon: 'BACON',
};

const ORDER = new Map<Topping, number>(TOPPINGS.map((t, i) => [t, i]));

/** Canonical menu order, de-duplicated. */
export function sortToppings(toppings: readonly Topping[]): Topping[] {
  return [...new Set(toppings)].sort((a, b) => (ORDER.get(a) ?? 0) - (ORDER.get(b) ?? 0));
}

export function isAllTheWay(toppings: readonly Topping[]): boolean {
  const set = new Set(toppings);
  return TOPPINGS.every((t) => set.has(t));
}

/** "SCATTERED, SMOTHERED, COVERED" · "SCATTERED WELL" · "ALL THE WAY" */
export function hashBrownWords(spec: HashBrownSpec): string[] {
  const scattered = spec.well ? 'SCATTERED WELL' : 'SCATTERED';
  if (isAllTheWay(spec.toppings)) return [scattered, 'ALL THE WAY'];
  return [scattered, ...sortToppings(spec.toppings).map((t) => TOPPING_INFO[t].lingo)];
}

export function hashBrownLingo(spec: HashBrownSpec): string {
  return hashBrownWords(spec).join(', ');
}

export function itemCount(order: OrderSpec): number {
  return (
    (order.hashBrowns ? 1 : 0) +
    (order.waffle ? 1 : 0) +
    (order.eggs ? 1 : 0) +
    (order.bacon ? 1 : 0)
  );
}

export function orderedKinds(order: OrderSpec): ItemKind[] {
  const kinds: ItemKind[] = [];
  if (order.hashBrowns) kinds.push('hashBrowns');
  if (order.waffle) kinds.push('waffle');
  if (order.eggs) kinds.push('eggs');
  if (order.bacon) kinds.push('bacon');
  return kinds;
}

export function isAllStar(order: OrderSpec): boolean {
  return itemCount(order) === 4;
}

export interface OrderLine {
  kind: ItemKind;
  /** The short "call" verb: DROP / PULL / MARK. */
  verb: string;
  text: string;
}

/**
 * Ticket lines in the style of the Waffle House "pull, drop, mark" call:
 * meats are pulled, hash browns and waffles are dropped, eggs are marked.
 */
export function orderLines(order: OrderSpec): OrderLine[] {
  const lines: OrderLine[] = [];
  if (order.hashBrowns)
    lines.push({ kind: 'hashBrowns', verb: 'DROP', text: hashBrownLingo(order.hashBrowns) });
  if (order.waffle) lines.push({ kind: 'waffle', verb: 'DROP', text: WAFFLE_LABEL[order.waffle] });
  if (order.eggs) lines.push({ kind: 'eggs', verb: 'MARK', text: EGG_LABEL[order.eggs] });
  if (order.bacon) lines.push({ kind: 'bacon', verb: 'PULL', text: 'BACON' });
  return lines;
}

/** One-line call used in the order bar and screen reader announcements. */
export function orderCall(order: OrderSpec): string {
  const parts = orderLines(order).map((l) =>
    l.kind === 'hashBrowns' ? `HASH BROWNS ${l.text}` : l.text,
  );
  const call = parts.join(' · ');
  return isAllStar(order) ? `ALL-STAR: ${call}` : call;
}

/** What Miss Dottie hollers across the counter (fed to speech synthesis). */
export function orderSpeech(order: OrderSpec): string {
  const bits: string[] = [];
  if (order.bacon) bits.push('pull one bacon');
  if (order.hashBrowns) {
    const words = hashBrownWords(order.hashBrowns).map((w) => w.toLowerCase());
    bits.push(`drop one hash brown, ${words.join(', ')}`);
  }
  if (order.waffle) bits.push(`drop one ${WAFFLE_LABEL[order.waffle].toLowerCase()}`);
  if (order.eggs) bits.push(`mark one ${EGG_LABEL[order.eggs].toLowerCase()}`);
  return bits.map((b) => b.charAt(0).toUpperCase() + b.slice(1)).join('. ') + '!';
}
